import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { CommunicationActivity } from 'app/models/entities/communication-activity';
import { CommunicationLogV2Response, CommunicationLogFilters, CommunicationStatusV2 } from 'app/models/entities/communication-status-v2';
import { map } from 'rxjs/operators';
import { EmailTemplate } from '../../models/entities/email-template';
import moment, { Moment } from 'moment-timezone';
import { Communications } from 'app/components/shared/http/request-interfaces';
import { UploadedFile } from '../../models/entities/uploaded-file';
import { PDFSource } from 'ng2-pdf-viewer';
import { HttpHeaders } from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { CsvExportService } from 'app/services/csv-export/csv-export.service';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';

export interface V2StatusInfo {
    event: string;
    class: string;
}

export interface ScheduledEmailDuplicate {
    id: number;
    subject: string;
    createdAt: string;
    scheduledSendAt: string;
    totalRecipients: number;
    overlappingRecipients: number;
}

export interface PdfExportCreateRequest {
    type: 'statements' | 'histories' | 'violations';
    unitIds: number[];
    options?: PdfExportOptions;
}

export interface PdfExportCreateResponse {
    exportId: number;
    status: string;
    totalUnits: number;
    message: string;
}

export interface PdfExportOptions {
    includeFuture?: boolean;
    currentOwnerOnly?: boolean;
    separatePdfPerCharge?: boolean;
}

export interface PdfExportStatusResponse {
    exportId: number;
    type: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    totalUnits: number;
    processedUnits: number;
    options?: PdfExportOptions;
    createdAt: string;
    downloadUrl?: string;
    expiresAt?: string;
    errorMessage?: string;
}

@Injectable({
    providedIn: 'root',
})
export class CommunicationService
{
    private static readonly STATUS_MAP: Record<string, V2StatusInfo> = {
        'scheduled': { event: 'Scheduled', class: 'event-scheduled badge badge-info' },
        'cancelled': { event: 'Cancelled', class: 'event-cancelled badge badge-dark' },
        'failed': { event: 'Failed', class: 'event-failed badge badge-danger' },
        'bounced': { event: 'Bounced', class: 'event-bounced badge badge-warning' },
        'clicked': { event: 'Clicked', class: 'event-clicked badge badge-primary' },
        'opened': { event: 'Opened', class: 'event-opened badge badge-success' },
        'delivered': { event: 'Delivered', class: 'event-delivered badge badge-info' },
        'sent': { event: 'Sent', class: 'event-sent badge badge-secondary' },
        'submitted': { event: 'Submitted', class: 'event-submitted badge' },
    };
    private static readonly DEFAULT_STATUS: V2StatusInfo = { event: 'Unknown', class: 'badge badge-secondary' };

    // Short-form labels for known provider failure messages; unmapped errors surface raw.
    private static readonly FAILURE_REASON_SHORT_FORMS: { pattern: RegExp, label: string }[] = [
        { pattern: /unsubscribed recipient/i, label: 'Recipient has unsubscribed' },
        { pattern: /unable to write bytes on the wire/i, label: 'Unable to reach email provider. Please try sending it again.' },
    ];

    // Text-message type values: activities store 'sms'; 'text' tolerated to match formatType().
    private static readonly SMS_TYPES: string[] = ['sms', 'text'];

    public openCommunicationSender$ = new Subject<{
        unitId: number,
        type: string
    }>();
    public preselectedUnitIds: number[] = [];
    private _http: ApplicationHttpClient;

    constructor(
        _http: ApplicationHttpClient,
        private _csvExportService: CsvExportService,
        private _legfiDate: LegfiDatePipe,
        // private mockDataService: MockDataService
    ) {
        this._http = _http;
    }

    /**
     * Get graph data for email graph for org for time range
     *
     * @param filters
     */
    public getEmailStatusLineGraph(filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgEmailStatusGraph(
                        jwt.orgId,
                ) + query,
        );

        return this._http.get(url);
    }

    getEmailStatusCounts(filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgEmailStatusCounts(
                        jwt.orgId,
                ) + query,
        );
        return this._http.get(url);
    }


    getEmailCategoryCounts(filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgEmailCategoryCounts(
                        jwt.orgId,
                ) + query,
        );
        return this._http.get(url);
    }

    getCommCountsDonutGraph(
            filters: { startDate: Moment, endDate: Moment },
            which: string,
    ): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgCommCountsSmsCalls(
                        jwt.orgId, which,
                ) + query,
        );
        return this._http.get(url);
    }


    /**
     * * Get graph data for for paper mail graph for org for time range
     *
     * @param filters
     */
    public getPaperMailGraphCounts(filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgPaperMailGraphCounts(
                        jwt.orgId,
                ) + query,
        );

        return this._http.get(url);
    }

    /**
     * Get status info for paper mail counts for org for time range
     *
     * @param filters
     */
    public getPaperMailStatusCounts(filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgPaperMailStatusCounts(
                        jwt.orgId,
                ) + query,
        );

        return this._http.get(url);
    }

    /**
     * Get available filters for the V2 comms log for a given org.
     * Uses communication_statuses + communication_activities only.
     */
    public getV2CommsLogFilters(orgId: number = null) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgCommsLogV2Filters(orgId || jwt.orgId),
        );

        return this._http.get(url);
    }

    /**
     * Get Communication Log V2 data (uses CommunicationStatus + CommunicationActivity)
     * NO CommunicationResponse involvement
     */
    public getV2CommunicationLogs(
        orgId: number,
        search: string = '',
        sortColumn: string = 'createdAt',
        sortDirection: string = 'desc',
        pageIndex: number = 1,
        perPage: number = 50,
        filters: CommunicationLogFilters = {}
    ): Observable<CommunicationLogV2Response> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        // Validate and constrain page index (prevent abuse)
        const validPage = Math.max(1, Math.min(Math.floor(pageIndex), 9999));

        // Validate and constrain per_page (DoS prevention)
        const validPerPage = Math.max(10, Math.min(Math.floor(perPage), 500));

        // Validate sort direction (only asc/desc allowed)
        const validDirection = ['asc', 'desc'].includes(sortDirection) ? sortDirection : 'desc';

        // Whitelist sort columns to prevent injection attacks
        const validColumns = ['createdAt', 'sender', 'recipient', 'sentTo', 'type', 'subject', 'category', 'sentAt', 'openedAt'];
        const validColumn = validColumns.includes(sortColumn) ? sortColumn : 'createdAt';

        const url = Routes.MakeLegFiCoreUrl(
            `/organizations/${orgId || jwt.orgId}/communications/v2`
        );

        const queryParams = [
            `page=${validPage}`,
            `search=${encodeURIComponent(search)}`,
            `column=${validColumn}`,
            `direction=${validDirection}`,
            `perPage=${validPerPage}`,
            `filters=${encodeURIComponent(JSON.stringify(filters))}`
        ];

        return this._http.get(`${url}?${queryParams.join('&')}`);
    }


    /**
     * Get saved email templates for this org.
     * @param organizationId
     */
    public getEmailTemplates(organizationId: number): Observable<EmailTemplate[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Organization(organizationId || jwt.orgId) + '/email-templates',
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((template) => {
                return new EmailTemplate(template);
            });
        }));
    }

    /**
     * Save content of this template.
     * @param organizationId
     * @param requestBody
     */
    public saveTemplate(organizationId: number, requestBody: any): Observable<number> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Organization(organizationId || jwt.orgId) + '/email-templates',
        );

        // SD-8196: this returned the whole response while declaring Observable<number>. The caller
        // assigned it to a `number` field and TypeScript raised nothing, because the declared type said
        // it already was one — so the object only revealed itself when it was concatenated into a URL as
        // "[object Object]", producing a 500 on the next Update Template click.
        //
        // saveEmailTemplate() responds {id: <int>, template: {...}} (EmailTemplateController::70), so the
        // id is a plain top-level field. Return it and the declared type becomes true.
        // The parameter carries a shape rather than `any` on purpose: with it, returning the whole
        // response is a COMPILE error instead of a runtime surprise, so this cannot regress the same way.
        return this._http.post(url, JSON.stringify(requestBody)).pipe(map((response: { id: number }) => {
            return response.id;
        }));
    }

    /**
     * Update content of this template.
     * @param organizationId
     * @param requestBody
     * @param templateId
     */
    public updateTemplate(organizationId: number, requestBody: any, templateId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Organization(organizationId || jwt.orgId) + '/email-templates/' + templateId,
        );

        return this._http.put(url, JSON.stringify(requestBody));
    }

    /**
     * Delete saved email template.
     * @param organizationId
     * @param templateId
     */
    public deleteTemplate(organizationId: number, templateId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Organization(organizationId || jwt.orgId) + '/email-templates/' + templateId,
        );

        return this._http.delete(url);
    }

    public getV2CommunicationLogsForMember(
        organizationId: number,
        userId: number,
    ): Observable<CommunicationLogV2Response> {
        return this.getV2CommunicationLogs(
            organizationId, '', 'createdAt', 'desc', 1, 100, { recipientId: [userId] }
        );
    }

    /**
     * Get V2 communication logs for a specific unit.
     * SD-4622: Uses unitId filter which fetches all historical owners on the backend,
     * ensuring communications to previous owners are included after ownership changes.
     */
    public getV2CommunicationLogsForUnit(
        organizationId: number,
        unitId: number,
        pageIndex: number = 1,
        perPage: number = 50,
    ): Observable<CommunicationLogV2Response> {
        // Use unitId filter to get all historical owner communications (SD-4622)
        return this.getV2CommunicationLogs(
            organizationId, '', 'createdAt', 'desc', pageIndex, perPage, { unitId }
        );
    }

    public getV2StatusInfo(status: CommunicationStatusV2): V2StatusInfo {
        return CommunicationService.STATUS_MAP[status.status] || CommunicationService.DEFAULT_STATUS;
    }

    public getV2StatusClass(status: CommunicationStatusV2): string {
        return this.getV2StatusInfo(status).class;
    }

    public getV2StatusText(status: CommunicationStatusV2): string {
        return this.getV2StatusInfo(status).event;
    }

    /**
     * Failure reason for a failed text message, mapped to a short-form label when the
     * provider message is a known one. Scoped to texts; null when no reason exists.
     */
    public getV2FailureReason(status: CommunicationStatusV2): string | null {
        if (status.status !== 'failed' || !CommunicationService.SMS_TYPES.includes(status.type)) {
            return null;
        }

        return this.formatFailureReason(status.statusData?.metadata?.lastError);
    }

    /**
     * Whether an activity type value represents a text message.
     */
    public isSmsActivityType(type: string | null | undefined): boolean {
        return !!type && CommunicationService.SMS_TYPES.includes(type);
    }

    /**
     * Map a raw provider failure message to its short-form label, if one is known.
     */
    public formatFailureReason(message: string | null | undefined): string | null {
        if (!message) {
            return null;
        }

        const shortForm = CommunicationService.FAILURE_REASON_SHORT_FORMS
            .find((mapping) => mapping.pattern.test(message));

        return shortForm ? shortForm.label : message;
    }

    /**
     * Build and download a 14-column CSV of communication statuses.
     *
     * Shared by the org-wide Communications Log export and the per-unit
     * Communications table export (SD-6464) so both produce an identical CSV.
     *
     * @param statuses
     * @param filename
     */
    public buildCommunicationsCsv(statuses: CommunicationStatusV2[], filename: string): void {
        const exportData = statuses.map((status) => {
            const unitAddress = status.recipientUnitAddress
                ? `${status.recipientUnitAddress.line1 || ''} ${status.recipientUnitAddress.line2 || ''} ${status.recipientUnitAddress.city || ''}, ${status.recipientUnitAddress.region || ''} ${status.recipientUnitAddress.postalCode || ''}`
                    .replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim()
                : '';

            return {
                activity_id: status.activityId,
                sender: status.senderName || 'PayHOA',
                recipient: status.recipientName,
                sent_to: status.recipientPhone || status.recipientAddress || status.recipientEmail || '',
                unit_title: status.recipientUnitTitle || '',
                unit_address: unitAddress,
                type: this.formatType(status.type),
                subject: status.subject || '',
                category: status.category || '',
                status: this.getV2StatusText(status),
                sent_at: this._legfiDate.transform(status.activityCreatedAt, 'MM/DD/YYYY h:mm a'),
                opened_at: status.openedAt ? this._legfiDate.transform(status.openedAt, 'MM/DD/YYYY h:mm a') : '',
                opens: status.openCount,
                clicks: status.clickCount,
            };
        });

        const options = {
            headers: [
                'activity_id',
                'sender',
                'recipient',
                'sent_to',
                'unit_title',
                'unit_address',
                'type',
                'subject',
                'category',
                'status',
                'sent_at',
                'opened_at',
                'opens',
                'clicks',
            ],
            showLabels: true,
        };

        this._csvExportService.generateCsv(exportData, filename, options);
    }

    public transformV2ToDialogFormat(status: CommunicationStatusV2): any {
        const responses = (Array.isArray(status.statusData?.events) ? status.statusData.events : [])
            .filter(event => event && typeof event === 'object' && event.occurredAt)
            .map((event: any) => ({
                event: event.type || 'unknown',
                createdAt: moment.utc(event.occurredAt).tz(LegFiJwtService.getTimezone()),
                category: event.source || null,
                additional: event.details || null,
            }));

        return {
            id: status.activityId,
            type: status.type || 'unknown',
            subject: status.subject || '',
            recipient: status.recipientId || null,
            recipientName: status.recipientName || 'Unknown',
            recipientEmail: status.recipientEmail || '',
            recipientAddress: status.recipientAddress || '',
            recipientPhone: status.recipientPhone || '',
            body: (status.activity && typeof status.activity === 'object')
                ? status.activity.body || null
                : null,
            responses: this.dedupeSubmittedResponses(responses),
            // Addresses the send recorded, so the dialog can list every recipient rather than
            // only those the provider reported an event for.
            recipients: Array.isArray(status.statusData?.recipients) ? status.statusData.recipients : [],
        };
    }

    /**
     * An email records several submit-class events (creation, mailer handoff,
     * provider acceptance) that all render as "Submitted" — display only the
     * earliest. Ties keep the first-recorded event. Stored events are unchanged.
     */
    public dedupeSubmittedResponses<T extends { event: string, createdAt: Moment }>(responses: T[]): T[] {
        const submitted = responses.filter(response => response.event === 'submitted');
        if (submitted.length <= 1) {
            return responses;
        }

        const earliest = submitted.reduce((a, b) => b.createdAt.valueOf() < a.createdAt.valueOf() ? b : a);

        return responses.filter(response => response.event !== 'submitted' || response === earliest);
    }

    /**
     * The provider reports events per recipient address onto one activity, so a
     * flat timeline interleaves addresses (an aux bounce's "Failed" beside the
     * primary's "Delivered"). Split responses into one section per address: the
     * primary first, then each auxiliary address, in the order the send recorded
     * them (falling back to order of first appearance for records sent before the
     * recipient list was captured).
     * The app-side Submitted covers the whole send, so a copy of it leads every
     * section; other address-less events stay in the primary section.
     *
     * @param recipients Addresses the send recorded, so every recipient is listed
     *                   even before the provider reports an event for it.
     */
    public groupResponsesByRecipientEmail<T extends { event: string, additional?: { email?: string } | null }>(
        responses: T[],
        primaryEmail: string,
        recipients: { email?: string, isPrimary?: boolean }[] = [],
    ): { email: string, isPrimary: boolean, responses: T[] }[] {
        const primaryLabel = (primaryEmail || '').trim();
        const primaryKey = primaryLabel.toLowerCase();
        const primaryGroup = { email: primaryLabel, isPrimary: true, responses: [] as T[] };
        const auxGroups = new Map<string, { email: string, isPrimary: boolean, responses: T[] }>();
        const recordedKeys = new Set<string>();
        let creationSubmitted: T = null;

        // Seed a section for every address the send recorded, so a recipient is listed from the
        // moment the email goes out rather than only once the provider reports on it. Older
        // records carry no recipient list and fall back to the events below.
        for (const recipient of (recipients || [])) {
            const email = (recipient?.email || '').trim();
            const key = email.toLowerCase();
            if (key === '' || key === primaryKey || auxGroups.has(key)) {
                continue;
            }

            auxGroups.set(key, { email, isPrimary: false, responses: [] });
            recordedKeys.add(key);
        }

        for (const response of responses) {
            const email = (response.additional?.email || '').trim().toLowerCase();
            if (email === '' || email === primaryKey) {
                primaryGroup.responses.push(response);
                if (email === '' && response.event === 'submitted' && creationSubmitted === null) {
                    creationSubmitted = response;
                }
                continue;
            }

            if (!auxGroups.has(email)) {
                auxGroups.set(email, { email: response.additional.email.trim(), isPrimary: false, responses: [] });
            }
            auxGroups.get(email).responses.push(response);
        }

        const auxSections = Array.from(auxGroups.values());
        if (creationSubmitted !== null) {
            // chronologically first, so prepending a copy per section keeps order
            auxSections.forEach(group => group.responses.unshift({ ...creationSubmitted }));
        }

        // A recorded recipient keeps its section even with nothing in it — being listed is the
        // point. Only sections derived from events are dropped when empty, as before.
        return [primaryGroup, ...auxSections].filter(
            group => group.responses.length > 0 || recordedKeys.has(group.email.trim().toLowerCase()),
        );
    }

    /**
     * Get rendered view of a communication log entry.
     * @param {number} organizationId
     * @param {number} activityId
     * @returns {any}
     */
    public getViewContent(organizationId: number, activityId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgCommunicationsView(organizationId || jwt.orgId, activityId),
        );

        return this._http.get(url);
    }

    /**
     * Send email to members.
     * @param {number} organizationId
     * @param {any} request
     * @returns {any}
     */
    public sendEmail(organizationId: number, request: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgCommsEmail(organizationId || jwt.orgId));
        const options: Object = {
            observe: 'response',
        };

        return this._http.post(url, JSON.stringify(request), options);
    }

    /**
     * Save email draft for the current user.
     * Replaces any existing draft for this user.
     * @param {number} organizationId
     * @param {any} request - Draft data (from, subject, message, memberIds, unitIds, attachments, auxiliary)
     * @returns {Observable<{data: {id: number}}>}
     */
    public saveDraft(organizationId: number, request: any): Observable<{data: {id: number}}> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/draft`
        );

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Get the current user's saved draft (if any).
     * @param {number} organizationId
     * @returns {Observable<any>}
     */
    public getDraft(organizationId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/draft`
        );

        return this._http.get(url);
    }

    /**
     * Delete a saved draft.
     * @param {number} organizationId
     * @param {number} draftId
     * @returns {Observable<any>}
     */
    public deleteDraft(organizationId: number, draftId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/draft/${draftId}`
        );

        return this._http.delete(url);
    }

    /**
     * Send or schedule an email from a draft.
     * If scheduledSendAt is provided, schedules for later. Otherwise sends immediately.
     * @param {number} organizationId
     * @param {any} request - {draftId: number, scheduledSendAt?: string (ISO 8601)}
     * @returns {Observable<any>}
     */
    public sendScheduledEmail(organizationId: number, request: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/send-scheduled`
        );

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Check whether scheduling this email would duplicate an existing scheduled
     * email (same subject + send time + at least one overlapping recipient).
     * Returns the matching duplicates so the UI can warn the admin (SD-6168).
     */
    public checkScheduledEmailDuplicates(
        organizationId: number,
        request: {subject: string; scheduledSendAt: string; memberIds: number[]},
    ): Observable<{data: ScheduledEmailDuplicate[]}> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/scheduled/duplicates-check`
        );

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Cancel a scheduled email (before it's sent).
     * @param {number} organizationId
     * @param {number} scheduledId
     * @returns {Observable<any>}
     */
    public cancelScheduledEmail(organizationId: number, scheduledId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/email/scheduled/${scheduledId}`
        );

        return this._http.delete(url);
    }

    /**
     * Send email to members.
     * @param {number} organizationId
     * @param {any} request
     * @param {number} memberId
     * @returns {any}
     */
    public sendSampleEmail(organizationId: number, request: any, memberId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgCommsSendSampleEmail(organizationId || jwt.orgId, memberId));
        const options: Object = {
            observe: 'response',
        };

        return this._http.post(url, JSON.stringify(request), options);
    }


    /**
     * Get content of a sample message with real values filled in to show them a preview.
     *
     * @param {any} msg is the mustached email content
     * @param {string} memberId is the sample member from this org that we are getting a sample for
     * @returns {any}
     */
    public previewSampleEmail(msg: any, memberId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgCommsSampleEmail(jwt.orgId, memberId));

        const request = {
            message: msg,
        };
        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Send SMS message to members.
     * @param {number} organizationId
     * @param {any} request
     * @returns {any}
     */
    public sendText(organizationId: number, request: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgCommsTexts(organizationId || jwt.orgId));
        const options: Object = {
            observe: 'response',
        };

        return this._http.post(url, JSON.stringify(request), options);
    }

    /**
     * Send or schedule phone calls to send.
     *
     * @param organizationId
     * @param requestBody
     */
    public sendPhoneCalls(organizationId: number, requestBody: Communications.SendPhoneCallRequest): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.OrgCommsCalls(organizationId || jwt.orgId),
                ),
                JSON.stringify(requestBody),
        );
    }


    /**
     * Create an async PDF export request.
     * Returns an exportId that can be polled for status.
     */
    public createPdfExport(organizationId: number, request: PdfExportCreateRequest): Observable<PdfExportCreateResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExports(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Get the status of a PDF export request.
     */
    public getPdfExportStatus(organizationId: number, exportId: number): Observable<PdfExportStatusResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExportStatus(organizationId || jwt.orgId, exportId),
        );

        return this._http.get(url);
    }

    /**
     * Get the download URL for a completed PDF export.
     */
    public getPdfExportDownloadUrl(organizationId: number, exportId: number): string {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        return Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExportDownload(organizationId || jwt.orgId, exportId),
        );
    }

    /**
     * Cancel a pending or processing PDF export.
     */
    public cancelPdfExport(organizationId: number, exportId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExportStatus(organizationId || jwt.orgId, exportId),
        );

        return this._http.delete(url);
    }

    /**
     * Get list of PDF exports for the current user.
     */
    public getPdfExports(organizationId: number): Observable<PdfExportStatusResponse[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExports(organizationId || jwt.orgId),
        );

        return this._http.get(url);
    }

    /**
     * Retry a stuck or failed PDF export.
     */
    public retryPdfExport(organizationId: number, exportId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExportRetry(organizationId || jwt.orgId, exportId),
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Download a completed PDF export
     */
    public downloadPdfExport(organizationId: number, exportId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgPdfExportDownload(organizationId || jwt.orgId, exportId),
        );

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.get(url, options);
    }

    public downloadViolations(organizationId: number, request: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgViolationsPdfDownload(organizationId || jwt.orgId),
        );

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify(request), options);
    }

    public generatePdfFromCommsLogEmail(orgId: number, request: any): Observable<{ blob: Blob, filename?: string, contentType?: string }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgCommsLogPdfDownload(orgId || jwt.orgId),
        );

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            responseType: 'blob' as 'blob',
            observe: 'response' as 'response' // This allows us to access headers
        };

        return this._http.post(url, JSON.stringify(request), httpOptions).pipe(
                map((response: HttpResponse<Blob>) => {
                    const contentDisposition = response.headers.get('Content-Disposition');
                    const contentType = response.headers.get('Content-Type');

                    let filename: string | undefined;

                    // Try to extract filename from Content-Disposition header
                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                        if (filenameMatch) {
                            filename = filenameMatch[1].replace(/['"]/g, '');
                        }
                    }

                    return {
                        blob: response.body!,
                        filename: filename,
                        contentType: contentType || undefined
                    };
                })
        );
    }

    public generateV2PdfFromCommsLogEmail(orgId: number, request: any): Observable<{ blob: Blob, filename?: string, contentType?: string }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.LegFiCore.OrgCommsLogV2PdfDownload(orgId || jwt.orgId),
        );

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            responseType: 'blob' as 'blob',
            observe: 'response' as 'response'
        };

        return this._http.post(url, JSON.stringify(request), httpOptions).pipe(
            map((response: HttpResponse<Blob>) => {
                const contentDisposition = response.headers.get('Content-Disposition');
                const contentType = response.headers.get('Content-Type');

                let filename: string | undefined;

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                return {
                    blob: response.body!,
                    filename: filename,
                    contentType: contentType || undefined
                };
            })
        );
    }

    public previewPaperStatement(
            organizationId: number,
            unitId: number,
            includeFuture: boolean = false,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgStatementPdfPreviewForUnit(jwt.orgId, unitId, includeFuture));

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify({}), options);
    }

    public deleteCommunications(organizationId: number, ids: number[]) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/communications')
                + '?organizationId=' + organizationId
                + '&ids=' + ids.join(',');

        return this._http.delete(url);
    }

    /**
     * Cancel a scheduled communication (e.g., amenity share booking email).
     *
     * @param organizationId The organization ID
     * @param activityId The communication activity ID
     */
    public cancelScheduledCommunication(organizationId: number, activityId: number): Observable<{ success: boolean, message: string }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/communications/${activityId}/cancel-scheduled`
        );

        return this._http.delete(url);
    }

    /**
     * Get the recipients for a scheduled communication.
     *
     * @param organizationId The organization ID
     * @param activityId The communication activity ID
     */
    public getScheduledRecipients(organizationId: number, activityId: number): Observable<ScheduledRecipientsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/organizations/${organizationId || jwt.orgId}/communications/${activityId}/scheduled-recipients`
        );

        return this._http.get(url);
    }

    /**
     * Format the type of communication for nicer presentation in tables.
     *
     * @param type
     */
    public formatType(type: string): string {
        const typeMap = {
            'email': 'Email',
            'text': 'Text',
            'sms': 'Text',
            'call': 'Call',
            'paper': 'On-Demand Statement',
            'letter': 'Custom PDF',
            'violation_letter': 'Violation Letter',
            'voting_ballot_letter': 'Voting Ballot Letter',
            'undeliverable': 'Undeliverable',
            'bill_pay': 'Bill Pay Check',
        };

        if (typeMap.hasOwnProperty(type)) {
            return typeMap[type];
        }

        return type;
    }

    /**
     * Format the last event of a communication for nicer presentation in tables.
     *
     * @param lastEvent
     */
    public formatEvent(lastEvent: string): string {
        let returnEvent = lastEvent;
        switch (lastEvent) {
            case 'Processed by PayHOA':
                returnEvent = 'Submitted';
                break;
            case 'unsubscribe':
                returnEvent = 'Unsubscribed';
                break;
            case 'bounce':
                returnEvent = 'Bounced - invalid email';
                break;
            case 'click':
                returnEvent = 'Clicked';
                break;
            case 'open':
                returnEvent = 'Opened';
                break;
            case 'processed':
                returnEvent = 'Queued';
                break;
            case 'in_transit':
                returnEvent = 'In Transit';
                break;
            case 'processed_for_delivery':
                returnEvent = 'Delivered';
                break;
            default:
                // Capitalize first letter for any unhandled events
                if (lastEvent && lastEvent.length > 0) {
                    returnEvent = lastEvent.charAt(0).toUpperCase() + lastEvent.slice(1);
                }
                break;
        }

        return returnEvent;
    }

    /**
     * Map last event wording to css class for coloring
     *
     * @param lastEvent
     */
    public formatEventClass(lastEvent: string): string {
        let cssClass = 'default';
        switch (lastEvent.toLowerCase()) {
            case 'bounce':
            case 'dropped':
            case 'deferred':
            case 'cancelled':
            case 'unsubscribe':
            case 'failed':
            case 'failed to send':
            case 'failed to send paper statement':
            case 'failed to send letter':
            case 'failed - no email on file':
            case 'busy':
            case 'undelivered':
            case 'no-answer':
            case 'cancelled, locked':
                cssClass = 'bad';
                break;
            case 'click':
            case 'open':
            case 'delivered':
            case 'completed':
                cssClass = 'good';
                break;
            case 'scheduled':
            case 'processed':
            case 'processing':
            case 'processed by payhoa':
            case 'sent':
                cssClass = 'default';
                break;
        }

        return cssClass;
    }

    /**
     * Find the first "open" event from list of communication responses
     * Used for audit purposes to show when an email was first opened
     *
     * @param responses (array of communication responses)
     */
    public getFirstOpenEvent(responses: any[]): any {
        if (!responses || responses.length === 0) {
            return null;
        }

        // find earliest "open" or "opened" event
        const openEvents = responses.filter(response =>
            response.event === 'open' || response.event === 'opened'
        );

        if (openEvents.length === 0) {
            return null;
        }

        // sort by createdAt to find earliest open event
        openEvents.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
                return a.createdAt.isBefore(b.createdAt) ? -1 : 1;
            }
            return 0;
        });

        return openEvents[0];
    }

    public getCommunicationActivityAttachment(
        organizationId: number,
        communicationActivityId: number,
        fileAttachment: UploadedFile
    ): Observable<PDFSource | string> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.CoreV2Module.CommunicationActivityAttachment(
            organizationId,
            communicationActivityId,
            fileAttachment.id
        ));

        // check for pdf or application/pdf file type
        if (fileAttachment?.fileType !== 'pdf' && fileAttachment?.fileType !== 'application/pdf') {
            return this._http.get(url);
        }

        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        return this._http.get(url, {
            headers: newHeaders,
            responseType: 'arraybuffer',
        });
    }
}

/**
 * Response type for scheduled recipients API
 */
export interface ScheduledRecipient {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    unitTitle: string | null;
}

export interface ScheduledRecipientsResponse {
    recipients: ScheduledRecipient[];
    totalCount: number;
    subject: string;
}
